import { useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { activeSessionIdAtom } from '@/atoms/liveTranscriptionAtom';
import { useIsMiniModeEnabled } from '@/hooks/useIsMiniModeEnabled';
import { useAudioGraphStore } from '@/lib/audio/useAudioGraphStore';
import { useSessionRecordingStore } from '@/lib/audio/useSessionRecordingStore/useSessionRecordingStore';
import type { MiniModeCloseReason } from '@/models/analytics/features/mini-mode';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { RecordingStatus } from '@heidi/schema/ml-scribe';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';

import type { PipOpenOptions } from '../picture-in-picture-provider.types';

const MEDIA_SESSION_ACTION = 'enterpictureinpicture';

interface UseAutoMediaSessionParams {
  openPip: (options?: PipOpenOptions) => Promise<void>;
  closePip: (reason?: MiniModeCloseReason) => void;
  isPipOpen: boolean;
}

/**
 * Registers a Media Session and drives PiP open/close around tab visibility
 * during an active recording.
 *
 * - First tab hidden → we open PiP directly (residual activation from the
 *   record-button click).
 * - Tab visible → we close PiP.
 * - Subsequent tab hidden → Chrome's video-conferencing auto-PiP fires the
 *   `enterpictureinpicture` Media Session handler. Eligibility: active
 *   getUserMedia + Media Session metadata + this handler + the per-site
 *   "automatic picture-in-picture" setting (default on). If eligibility
 *   isn't met, PiP doesn't reopen — requestWindow() can't run without user
 *   activation, which a visibilitychange event doesn't supply.
 * - Recording stops → full teardown (metadata, audio element, PiP window).
 */
export function useAutoMediaSession({
  openPip,
  closePip,
  isPipOpen,
}: UseAutoMediaSessionParams): void {
  const intl = useIntl();
  const isMiniModeEnabled = useIsMiniModeEnabled();
  const status = useSessionRecordingStore((s) => s.status);
  const micSourceStream = useAudioGraphStore((s) => s.micSourceStream);
  const activeSessionId = useAtomValue(activeSessionIdAtom);

  // Stable refs — avoids tearing down the effect when callbacks or
  // intl change identity.
  const openPipRef = useRef(openPip);
  const closePipRef = useRef(closePip);
  const isPipOpenRef = useRef(isPipOpen);
  const intlRef = useRef(intl);
  openPipRef.current = openPip;
  closePipRef.current = closePip;
  isPipOpenRef.current = isPipOpen;
  intlRef.current = intl;

  // Retain the last active session id so cleanup can read the session's
  // recording_status from the query cache — `activeSessionIdAtom` is cleared
  // on both pause AND stop, so we can't rely on the live atom value inside
  // the teardown path.
  const lastActiveSessionIdRef = useRef<string | null>(null);
  if (activeSessionId) {
    lastActiveSessionIdRef.current = activeSessionId;
  }

  const isActive = isMiniModeEnabled && status === 'RECORDING';
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  useEffect(() => {
    if (!isActive || !micSourceStream || !navigator.mediaSession) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: 'Heidi',
      artist: intlRef.current.formatMessage({
        defaultMessage: 'Recording in progress',
        id: 'pipAutoMediaSession.recordingInProgress',
      }),
    });
    navigator.mediaSession.playbackState = 'playing';

    // A muted <audio> bound to the mic stream is required for Chrome to
    // recognise an active media session.
    const audioEl = document.createElement('audio');
    audioEl.muted = true;
    audioEl.srcObject = micSourceStream;
    audioEl.play().catch((error) => {
      logger.error('[useAutoMediaSession] Failed to play audio element', error);
    });

    // First tab-hidden opens PiP using residual activation from the
    // record-button click. Tab-visible closes PiP. Subsequent reopens come
    // from Chrome's auto-PiP firing the action handler below — a
    // visibilitychange event can't supply activation for requestWindow().
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && !isPipOpenRef.current) {
        void openPipRef.current({ trigger: 'tab_hidden', disallowReturnToOpener: false });
      } else if (document.visibilityState === 'visible' && isPipOpenRef.current) {
        closePipRef.current('tab_visible');
      }
    };

    try {
      navigator.mediaSession.setActionHandler(MEDIA_SESSION_ACTION, () => {
        if (!isPipOpenRef.current) {
          void openPipRef.current({
            trigger: 'media_session_action',
            disallowReturnToOpener: false,
          });
        }
      });
    } catch (error) {
      logger.warn(
        '[useAutoMediaSession] enterpictureinpicture action not supported',
        error
      );
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);

      try {
        navigator.mediaSession.setActionHandler(MEDIA_SESSION_ACTION, null);
      } catch {
        // noop
      }

      navigator.mediaSession.playbackState = 'none';
      navigator.mediaSession.metadata = null;

      audioEl.pause();
      audioEl.srcObject = null;

      // Only close PiP when recording fully stops — not when paused or
      // when micSourceStream changes (e.g. device switch mid-recording),
      // since requestWindow() can't reopen without user activation.
      // Timer seconds are preserved on some stop paths (scribe
      // `_stopOwnRecording` uses `onPause`, Chronicle stop uses
      // `onStopWithDuration`), so we read the session's `recording_status`
      // from the query cache instead — the mutation's optimistic `onMutate`
      // writes PAUSED/IDLE before the teardown runs.
      const sessionId = lastActiveSessionIdRef.current;
      const sessionFromCache = sessionId
        ? reactQueryClient.getQueryData<QuerySessionResponse>([
            GET_SESSION_BY_ID,
            { sessionId },
          ])
        : null;
      const isPaused = sessionFromCache?.recording_status === RecordingStatus.PAUSED;
      if (!isActiveRef.current && !isPaused) {
        closePipRef.current();
      }
    };
  }, [isActive, micSourceStream]);
}
